import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { BookingBand } from "@/components/site/BookingBand";
import { Sparkle } from "@/components/site/Sparkle";
import { IMG, HAIR_SERVICES, BOOKING_URL } from "@/data/site";

const MenuRow = ({ item, index }) => (
  <Reveal delay={0.05 * index}>
    <div
      data-testid={`hair-menu-item-${index}`}
      className="group grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 items-center py-6 border-b border-stone hover:border-gold transition-colors duration-500"
    >
      <div className="sm:col-span-6 flex items-center gap-4 sm:gap-5">
        <span className="hidden md:block font-display text-lg text-champagne w-7 shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 overflow-hidden border border-stone group-hover:border-gold transition-colors duration-500">
          <img
            src={item.img}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          />
        </div>
        <h3 className="font-display text-2xl sm:text-3xl text-espresso group-hover:text-gold group-hover:translate-x-1.5 transition-all duration-500">
          {item.name}
        </h3>
      </div>
      <p className="sm:col-span-4 text-deep/70 text-sm leading-relaxed">{item.desc}</p>
      <div className="sm:col-span-2 sm:text-right">
        <span className="font-display text-xl text-gold whitespace-nowrap">{item.duration}</span>
      </div>
    </div>
  </Reveal>
);

export default function ServicesHair() {
  return (
    <>
      <PageHero
        testId="services-hair-hero"
        eyebrow="Services — Hair"
        titleLines={["Cut. Colour.", "Care."]}
        quote="Every service begins with a conversation and ends with a result you'll want to keep."
        image={IMG.hair1}
        imageAlt="A stylist finishing a client's hair at the S & Z studio"
      />

      <section data-testid="hair-menu" className="bg-ivory py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal className="mb-12">
            <Eyebrow>The Hair Menu</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-espresso mt-4">
              Considered Work, <span className="italic text-gold">Honest Pricing</span>
            </h2>
            <p className="text-deep/60 text-sm mt-5 max-w-xl">
              Timings are estimates — your specialist confirms the exact duration and a personal quote at consultation.
            </p>
          </Reveal>
          <div>
            {HAIR_SERVICES.map((s, i) => (
              <MenuRow key={s.name} item={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section data-testid="hair-philosophy" className="bg-espresso py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <Eyebrow light>Our Approach</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-ivory mt-4 leading-tight">
              Hair That Behaves <span className="italic text-gold">on a Tuesday</span>
            </h2>
            <p className="text-ivory/65 text-base sm:text-lg mt-6 leading-relaxed">
              We cut for how your hair lives, not just how it photographs. Shape,
              weight, and grow-out are planned together — so week six looks as
              intentional as day one.
            </p>
            <ul className="mt-8 space-y-3">
              {["Professional-grade colour lines only", "Bond protection built into every lightening service", "A take-home care plan, never a hard sell"].map((t) => (
                <li key={t} className="flex items-center gap-3 font-sans uppercase tracking-[0.16em] text-[11px] text-champagne/90">
                  <Sparkle size={9} /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-5">
              <img src={IMG.hair2} alt="Hand-painted balayage in progress" loading="lazy" className="w-full h-64 sm:h-80 object-cover border border-ivory/15 img-frame mt-10" />
              <img src={IMG.hair4} alt="Precision finishing at the chair" loading="lazy" className="w-full h-64 sm:h-80 object-cover border border-ivory/15 img-frame" />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="hair-booking-callout" className="bg-ivory py-20 sm:py-28 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <Reveal>
            <p className="font-quote italic text-2xl sm:text-3xl text-deep">
              "Not sure what your hair needs? Book a consultation — the
              conversation is always complimentary."
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-8">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hair-consultation-cta"
              aria-label="Book a consultation (opens in a new tab)"
              className="inline-block font-sans uppercase tracking-[0.2em] text-xs font-medium px-8 py-4 border border-gold text-gold hover:bg-gold hover:text-espresso transition-all duration-300"
            >
              Book a Hair Consultation
            </a>
          </Reveal>
        </div>
      </section>

      <BookingBand />
    </>
  );
}
